<template>
  <section class="sending-domains">
    <div class="sending-domains__head">
      <div>
        <h2 class="h6 mb-0">Sending domains</h2>
        <p class="text-muted small mb-0">
          Add your own domain so email is sent — and DKIM &amp; SPF signed — from
          you, not a shared address. We recommend a dedicated subdomain.
        </p>
      </div>
      <div class="sending-domains__head-actions">
        <button class="btn btn-outline-primary btn-sm" :disabled="adding" @click="openAdd">
          <CygnusIcon name="plus" class="me-1" />Add domain
        </button>
        <button class="btn btn-link btn-sm" :disabled="busy.load" @click="refresh">
          <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
          <span v-else><CygnusIcon name="refresh" /></span>
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small mt-2">{{ error }}</div>

    <!-- Resolved effective sending domain (tenant → platform default →
         unconfigured). Shows the REAL domain this workspace sends on + its
         verification status; never the Mailgun account sandbox. -->
    <div v-if="resolved" class="sd-resolved" :class="'sd-resolved--' + resolved.source">
      <CygnusIcon :name="resolvedIcon" class="me-2" />
      <div class="sd-resolved__body">
        <template v-if="resolved.source === 'unconfigured'">
          <strong>No sending domain configured.</strong>
          Add and verify a domain below before you can send.
        </template>
        <template v-else>
          <span>
            Currently sending on
            <code>{{ resolved.domain }}</code>
            <span class="sd-pill" :class="'sd-pill--' + (resolved.verified ? 'verified' : resolved.state)">
              {{ resolved.verified ? 'Verified' : stateLabel(resolved.state) }}
            </span>
          </span>
          <span class="sd-resolved__note text-muted small">{{ resolved.label }}</span>
        </template>
      </div>
    </div>

    <!-- Add flow -->
    <div v-if="adding" class="sd-add card mt-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start">
          <h6 class="mb-0">Add a sending domain</h6>
          <button class="btn btn-link btn-sm" @click="closeAdd">Close</button>
        </div>

        <!-- Step 1: enter the domain -->
        <div v-if="!created" class="sd-add__step mt-2">
          <div class="sd-recommend">
            <CygnusIcon name="bolt" class="me-2" />
            <div>
              <strong>Use a dedicated subdomain — <code>mg.yourcompany.com</code>.</strong>
              It keeps your company email untouched: no risk to your existing MX
              or SPF, and Mailgun controls only the subdomain's DNS.
            </div>
          </div>

          <label class="form-label small text-muted mt-3">Domain to add</label>
          <div class="d-flex gap-2 align-items-start">
            <input
              v-model="newDomain"
              type="text"
              class="form-control form-control-sm"
              placeholder="mg.yourcompany.com"
              @keyup.enter="submitAdd"
            >
            <button class="btn btn-primary btn-sm text-nowrap" :disabled="busy.add || !newDomain.trim()" @click="submitAdd">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.add" />Create
            </button>
          </div>
          <label class="form-check mt-2 small">
            <input v-model="newInbound" type="checkbox" class="form-check-input">
            <span class="form-check-label">Also receive inbound mail on this domain (adds MX records)</span>
          </label>
          <p v-if="addError" class="sd-error mt-2">
            {{ addError }}<span v-if="addHint"> — {{ addHint }}</span>
          </p>
        </div>

        <!-- Step 2: show the DNS records -->
        <div v-else class="sd-add__step mt-3">
          <p class="small mb-2">
            Add these records at your DNS provider, then check below. We re-check
            automatically for 48 hours.
          </p>

          <div v-for="w in warnings" :key="w" class="alert alert-warning py-2 small mb-2">
            <CygnusIcon name="warning" class="me-1" />{{ w }}
          </div>

          <SendingDomainRecords :records="created.records" @copy="copyValue" />

          <div class="d-flex align-items-center gap-2 mt-3">
            <button class="btn btn-primary btn-sm" :disabled="busy.check" @click="check(created.id)">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.check" />
              <CygnusIcon name="shield" class="me-1" v-else />I've added these — check now
            </button>
            <span class="text-muted small">We also re-check automatically for 48h.</span>
          </div>
          <p v-if="copiedNote" class="sd-copied small mt-2">{{ copiedNote }}</p>
        </div>
      </div>
    </div>

    <!-- Domain list -->
    <div v-if="!busy.load && domains.length === 0 && !adding" class="text-muted small mt-3">
      <template v-if="resolved && resolved.source === 'platform'">
        No sending domains of your own yet. Email currently goes out on the shared
        platform domain <code>{{ resolved.domain }}</code> — add your own to send as you.
      </template>
      <template v-else-if="resolved && resolved.source === 'unconfigured'">
        No sending domain configured. Add one to start sending.
      </template>
      <template v-else>
        No sending domains yet. All email uses the shared default domain until you add one.
      </template>
    </div>

    <div v-for="d in domains" :key="d.id" class="sd-row card mt-2">
      <div class="card-body py-2">
        <div class="sd-row__head" @click="toggle(d.id)">
          <div class="sd-row__title">
            <CygnusIcon :name="expanded === d.id ? 'chevron-down' : 'chevron-right'" />
            <code>{{ d.domain }}</code>
            <span class="sd-pill" :class="'sd-pill--' + d.state">{{ stateLabel(d.state) }}</span>
            <span class="sd-records-count text-muted small">{{ validCount(d) }}/{{ (d.records || []).length }} records valid</span>
          </div>
          <div class="sd-row__actions" @click.stop>
            <button class="btn btn-outline-secondary btn-sm" :disabled="busy.check" @click="check(d.id)">
              <CygnusIcon name="shield" class="me-1" />Check now
            </button>
            <button class="btn btn-outline-danger btn-sm" @click="confirmRemove(d)">
              <CygnusIcon name="trash" />
            </button>
          </div>
        </div>

        <div v-if="expanded === d.id" class="sd-row__body mt-2">
          <SendingDomainRecords :records="d.records" @copy="copyValue" />

          <p v-if="d.state === 'verified'" class="sd-ok small mt-2">
            <CygnusIcon name="check-circle" class="me-1" />Verified — email from this
            domain is DKIM &amp; SPF signed.
          </p>

          <!-- Dedicated IP (io-domains M3) — verified domains only -->
          <div v-if="d.state === 'verified'" class="sd-ip mt-2">
            <div v-if="d.dedicated_ip" class="d-flex align-items-center gap-2 flex-wrap">
              <span class="small text-muted">Dedicated IP:</span>
              <code>{{ d.dedicated_ip }}</code>
              <button class="btn btn-link btn-sm p-0 text-danger" :disabled="busy.ip === d.id" @click="removeIp(d)">
                <CygnusIcon name="spinner" :spin="true" v-if="busy.ip === d.id" />
                <span v-else>Remove</span>
              </button>
            </div>
            <div v-else-if="dedicatedIp.enabled" class="d-flex align-items-center gap-2 flex-wrap">
              <select v-model="ipSelections[d.id]" class="form-select form-select-sm" style="max-width:220px">
                <option value="" disabled>Choose a dedicated IP…</option>
                <option v-for="ip in dedicatedIp.ips" :key="ip" :value="ip">{{ ip }}</option>
              </select>
              <button class="btn btn-outline-primary btn-sm" :disabled="busy.ip === d.id || !ipSelections[d.id]" @click="assignIp(d)">
                <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.ip === d.id" />Assign
              </button>
            </div>
            <p v-else class="small text-muted mb-0">
              <CygnusIcon name="lock" class="me-1" />Dedicated IPs are included on Scale, or {{ dedicatedIpAddonText }}.
            </p>
            <p v-if="ipError[d.id]" class="sd-error small mt-1 mb-0">{{ ipError[d.id] }}</p>
          </div>

          <div v-else class="sd-help small mt-2">
            <strong>Not verified yet?</strong> The usual causes:
            <ul class="mb-0">
              <li>A <strong>proxied CNAME</strong> (e.g. Cloudflare orange-cloud) on the tracking record — set it to DNS-only.</li>
              <li>A <strong>duplicate SPF</strong> record — there must be exactly one <code>v=spf1</code> TXT per host; merge Mailgun's include into it.</li>
              <li>A <strong>split DKIM TXT</strong> — the long key must be one value; some UIs truncate at 255 chars.</li>
              <li>Records added at a <strong>parked / wrong DNS zone</strong> — check they're on the zone that actually serves the domain.</li>
            </ul>
          </div>

          <p v-if="d.next_check_at && d.state !== 'verified' && d.state !== 'failed'" class="text-muted small mt-2">
            Next automatic check {{ fromNow(d.next_check_at) }}.
          </p>
          <p v-if="d.state === 'failed'" class="sd-error small mt-2">
            Automatic checks paused after repeated failures. Fix the records above
            and press <strong>Check now</strong> to resume.
          </p>
        </div>
      </div>
    </div>

    <!-- Remove confirm (inline, no native dialog) -->
    <div v-if="removing" class="sd-confirm card mt-2">
      <div class="card-body py-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <span class="small">Remove <code>{{ removing.domain }}</code>? It won't be deleted from Mailgun — an operator can retire it there.</span>
        <div class="d-flex gap-2">
          <button class="btn btn-link btn-sm" @click="removing = null">Cancel</button>
          <button class="btn btn-danger btn-sm" :disabled="busy.remove" @click="doRemove">Remove</button>
        </div>
      </div>
    </div>

    <input ref="fallbackInput" type="text" class="sd-copy-fallback" aria-hidden="true" tabindex="-1">
  </section>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import { addonForMeter, addonPriceText, loadAddonCatalogue } from "./util/addonPrice";

// Small inline sub-component: the DNS records table with copy buttons + valid ticks.
const SendingDomainRecords = {
  name: "SendingDomainRecords",
  props: { records: { type: Array, default: () => [] } },
  emits: ["copy"],
  methods: {
    tick(valid) { return valid ? "✓" : "•"; },
  },
  template: `
    <div class="sd-records">
      <div class="sd-records__row sd-records__row--head">
        <span>Type</span><span>Host / Name</span><span>Value</span><span>Valid</span><span></span>
      </div>
      <div v-for="(r, i) in records" :key="i" class="sd-records__row">
        <span class="sd-records__type">{{ r.record_type }}</span>
        <span class="sd-records__name"><code>{{ r.name }}</code></span>
        <span class="sd-records__value"><code>{{ r.value }}</code></span>
        <span class="sd-records__valid" :class="r.valid ? 'is-valid' : 'is-pending'">
          {{ r.valid ? '✓' : '•' }}
        </span>
        <span class="sd-records__copy">
          <button type="button" class="btn btn-link btn-sm p-0" title="Copy value" @click="$emit('copy', r.value)">
            <CygnusIcon name="file" />
          </button>
        </span>
      </div>
    </div>
  `,
};

export default {
  name: "SendingDomains",
  components: { SendingDomainRecords },
  data() {
    return {
      api: useApi(),
      domains: [],
      resolved: null,   // { source, domain, verified, state, label } — effective sending domain
      busy: { load: false, add: false, check: false, remove: false, ip: null },
      error: null,
      dedicatedIp: { enabled: false, ips: [] },
      dedicatedIpPrice: "",   // from the add-on catalogue (Hermes's charge, VAT included)
      ipSelections: {},
      ipError: {},
      adding: false,
      newDomain: "",
      newInbound: false,
      addError: "",
      addHint: "",
      created: null,       // the freshly-added domain (records to show)
      warnings: [],
      expanded: null,
      removing: null,
      copiedNote: "",
      copyTimer: null,
    };
  },
  computed: {
    dedicatedIpAddonText() {
      return this.dedicatedIpPrice ? `${this.dedicatedIpPrice} as an add-on` : "available as an add-on";
    },
    resolvedIcon() {
      if (!this.resolved) return "info-circle";
      if (this.resolved.source === "unconfigured") return "warning";
      return this.resolved.verified ? "check-circle" : "clock";
    },
  },
  mounted() { this.refresh(); this.loadDedicatedIpPrice(); },
  beforeUnmount() { clearTimeout(this.copyTimer); },
  methods: {
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/email/domains");
        const payload = res?.data ?? res;
        this.domains = Array.isArray(payload?.data) ? payload.data : [];
        this.dedicatedIp = {
          enabled: !!payload?.dedicated_ip?.enabled,
          ips: Array.isArray(payload?.dedicated_ip?.ips) ? payload.dedicated_ip.ips : [],
        };
      } catch (e) { this.error = e?.response?.data?.error || e?.message || "Failed to load sending domains."; }
      this.busy.load = false;
      this.loadResolved();
    },
    /** The dedicated IP add-on's price. Silent on failure: the copy drops the price. */
    async loadDedicatedIpPrice() {
      const { catalogue, currency } = await loadAddonCatalogue(this.api);
      this.dedicatedIpPrice = addonPriceText(addonForMeter(catalogue, "dedicated_ip"), currency);
    },
    async loadResolved() {
      try {
        const res = await this.api.get("/api/v1/admin/email/sending-domain");
        const payload = res?.data ?? res;
        this.resolved = payload?.resolved ?? null;
      } catch (e) { /* non-fatal — the domain list is the primary surface */ }
    },
    openAdd() {
      this.adding = true; this.created = null; this.warnings = [];
      this.newDomain = ""; this.newInbound = false; this.addError = ""; this.addHint = "";
    },
    closeAdd() { this.adding = false; this.created = null; this.refresh(); },
    async submitAdd() {
      const domain = this.newDomain.trim().toLowerCase();
      if (!domain) return;
      this.busy.add = true; this.addError = ""; this.addHint = "";
      try {
        const res = await this.api.post("/api/v1/admin/email/domains", { domain, inbound: this.newInbound });
        const payload = res?.data ?? res;
        if (payload?.domain) {
          this.created = payload.domain;
          this.warnings = payload.warnings || [];
          await this.refresh();
        } else {
          this.addError = payload?.error || "Failed to add domain.";
        }
      } catch (e) {
        this.addError = e?.response?.data?.error || e?.message || "Failed to add domain.";
        this.addHint = e?.response?.data?.hint || "";
      }
      this.busy.add = false;
    },
    async check(id) {
      this.busy.check = true; this.error = null;
      try {
        const res = await this.api.post(`/api/v1/admin/email/domains/${id}/check`, {});
        const payload = res?.data ?? res;
        const updated = payload?.domain;
        if (updated) {
          this.applyUpdate(updated);
          if (this.created && this.created.id === id) this.created = updated;
          if (payload.backed_off) this.copiedNote = "Mailgun is rate-limiting checks — we'll retry shortly.";
        } else if (payload?.error) {
          this.error = payload.error;
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Check failed.";
      }
      this.busy.check = false;
    },
    applyUpdate(updated) {
      const i = this.domains.findIndex((d) => d.id === updated.id);
      if (i >= 0) this.domains.splice(i, 1, updated); else this.domains.unshift(updated);
    },
    confirmRemove(d) { this.removing = d; },
    async doRemove() {
      if (!this.removing) return;
      this.busy.remove = true; this.error = null;
      try {
        const res = await this.api.delete(`/api/v1/admin/email/domains/${this.removing.id}`);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.domains = this.domains.filter((d) => d.id !== this.removing.id);
          this.removing = null;
        } else {
          this.error = payload?.error || "Could not remove domain.";
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Could not remove domain.";
      }
      this.busy.remove = false;
    },
    async assignIp(d) {
      const ip = (this.ipSelections[d.id] || "").trim();
      if (!ip) return;
      this.busy.ip = d.id; this.ipError = { ...this.ipError, [d.id]: "" };
      try {
        const res = await this.api.put(`/api/v1/admin/email/domains/${d.id}/ip`, { ip });
        const payload = res?.data ?? res;
        if (payload?.assignment) {
          this.applyUpdate({ ...d, dedicated_ip: payload.assignment.ip });
        } else {
          this.ipError = { ...this.ipError, [d.id]: payload?.error || "Could not assign the IP." };
        }
      } catch (e) {
        this.ipError = { ...this.ipError, [d.id]: e?.response?.data?.error || e?.message || "Could not assign the IP." };
      }
      this.busy.ip = null;
    },
    async removeIp(d) {
      this.busy.ip = d.id; this.ipError = { ...this.ipError, [d.id]: "" };
      try {
        const res = await this.api.delete(`/api/v1/admin/email/domains/${d.id}/ip?ip=${encodeURIComponent(d.dedicated_ip)}`);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.applyUpdate({ ...d, dedicated_ip: null });
        } else {
          this.ipError = { ...this.ipError, [d.id]: payload?.error || "Could not unassign the IP." };
        }
      } catch (e) {
        this.ipError = { ...this.ipError, [d.id]: e?.response?.data?.error || e?.message || "Could not unassign the IP." };
      }
      this.busy.ip = null;
    },
    toggle(id) { this.expanded = this.expanded === id ? null : id; },
    async copyValue(value) {
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value); copied = true;
        }
      } catch (e) { /* fall through */ }
      if (!copied) {
        const input = this.$refs.fallbackInput;
        if (input) { input.value = value; input.focus(); input.select(); try { document.execCommand("copy"); } catch (e) { /* ignore */ } }
      }
      this.copiedNote = "Copied to clipboard.";
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copiedNote = ""; }, 1400);
    },
    validCount(d) { return (d.records || []).filter((r) => r.valid).length; },
    stateLabel(s) {
      return { created: "Created", records_pending: "Add records", verifying: "Verifying", verified: "Verified", failed: "Action needed" }[s] || s;
    },
    fromNow(ts) {
      const secs = ts * 1000 - Date.now();
      if (secs <= 0) return "shortly";
      const mins = Math.round(secs / 60000);
      if (mins < 60) return `in ~${Math.max(1, mins)} min`;
      return `in ~${Math.round(mins / 60)} h`;
    },
  },
};
</script>

<style>
.sending-domains { margin-top: var(--m-space-6, 32px); }
.sending-domains__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.sending-domains__head-actions { display: flex; align-items: center; gap: 6px; }

.sending-domains .sd-recommend {
  display: flex; align-items: flex-start; gap: 4px;
  background: var(--m-accent-soft, #EFF6FF); color: var(--m-text-primary, #0F172A);
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 10px 12px; font-size: 0.82rem;
}
.sending-domains .sd-recommend code { color: var(--m-accent, #2563EB); }

.sending-domains .sd-resolved {
  display: flex; align-items: flex-start; gap: 4px; margin-top: 12px;
  background: var(--m-bg-subtle, #F8FAFC); color: var(--m-text-primary, #0F172A);
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 10px 12px; font-size: 0.82rem;
}
.sending-domains .sd-resolved--unconfigured { background: #FEF3C7; border-color: #FDE68A; color: #92400E; }
.sending-domains .sd-resolved__body { display: flex; flex-direction: column; gap: 2px; }
.sending-domains .sd-resolved__note { margin-top: 1px; }
.sending-domains .sd-resolved code { color: var(--m-accent, #2563EB); }
.sending-domains .sd-pill--platform { background: #E0F2FE; color: #075985; }

.sending-domains .sd-error { color: var(--m-status-danger, #B91C1C); }
.sending-domains .sd-ok, .sending-domains .sd-copied { color: var(--m-status-success, #15803D); }
.sending-domains .sd-copied { color: var(--m-text-secondary, #475569); }

.sending-domains .sd-row__head { display: flex; justify-content: space-between; align-items: center; gap: 8px; cursor: pointer; flex-wrap: wrap; }
.sending-domains .sd-row__title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sending-domains .sd-row__actions { display: flex; gap: 6px; }

.sending-domains .sd-pill {
  padding: 1px 9px; border-radius: 100px; font-size: 0.7rem; font-weight: 600;
  background: var(--m-bg-subtle, #F1F5F9); color: var(--m-text-secondary, #475569);
}
.sending-domains .sd-pill--verified { background: #DCFCE7; color: var(--m-status-success, #166534); }
.sending-domains .sd-pill--verifying { background: #FEF9C3; color: #854D0E; }
.sending-domains .sd-pill--records_pending { background: #E0F2FE; color: #075985; }
.sending-domains .sd-pill--failed { background: #FEE2E2; color: var(--m-status-danger, #991B1B); }

.sending-domains .sd-records { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px); overflow: hidden; overflow-x: auto; }
.sending-domains .sd-records__row {
  display: grid; grid-template-columns: 72px 1.4fr 2fr 52px 40px; gap: 8px;
  padding: 7px 10px; align-items: center; border-top: 1px solid var(--m-border, #E2E8F0);
  font-size: 0.78rem; min-width: 560px;
}
.sending-domains .sd-records__row--head { border-top: 0; background: var(--m-bg-subtle, #F8FAFC); color: var(--m-text-tertiary, #64748B); font-weight: 600; }
.sending-domains .sd-records__value code, .sending-domains .sd-records__name code { word-break: break-all; white-space: normal; }
.sending-domains .sd-records__valid { text-align: center; font-weight: 700; }
.sending-domains .sd-records__valid.is-valid { color: var(--m-status-success, #15803D); }
.sending-domains .sd-records__valid.is-pending { color: var(--m-text-tertiary, #94A3B8); }

.sending-domains .sd-help ul { padding-left: 18px; margin-top: 4px; }
.sending-domains .sd-copy-fallback { position: absolute; left: -9999px; opacity: 0; }
</style>
